/**
 * PDF export utility built on jspdf + jspdf-autotable.
 * Renders a "Credit Card Control Center" branded corporate report:
 *   • Navy header strip with brand + generated timestamp
 *   • Report title
 *   • Applied filters summary
 *   • Row count / totals summary
 *   • Landscape table with alternating row shading
 *   • Page numbers in footer
 *
 * Columns share the same { label, value, money?, align? } shape used
 * by the CSV exporter, so callers can pass a single columns array.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtMoney } from "@/lib/api";

const NAVY = [28, 41, 61];        // #1c293d — matches app's --brand-navy
const STONE_900 = [28, 25, 23];   // near-black body
const STONE_500 = [120, 113, 108]; // muted meta
const STONE_100 = [245, 245, 244]; // zebra shade
const ACCENT = [180, 83, 9];      // amber-700 accent line

function nowStamp() {
    const d = new Date();
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
}

function slug(s) {
    return String(s || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// jsPDF's default Helvetica does not include the ₹ (U+20B9) glyph — it renders
// as garbled superscripts. Swap for "Rs." in every string headed to the PDF.
function sanitizePdfText(s) {
    if (s === null || s === undefined) return "";
    return String(s).replace(/₹\s?/g, "Rs. ").replace(/\u2192/g, "->");
}

function renderCell(row, col) {
    const raw = typeof col.value === "function" ? col.value(row) : row[col.value];
    if (raw === null || raw === undefined || raw === "") return col.money ? "—" : "";
    if (col.money) {
        const n = Number(raw);
        if (!Number.isFinite(n)) return sanitizePdfText(raw);
        return sanitizePdfText(fmtMoney(n));
    }
    return sanitizePdfText(raw);
}

function drawHeader(doc, title, subtitle) {
    const w = doc.internal.pageSize.getWidth();
    // Navy strip
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, w, 26, "F");
    // VAULT wordmark (light on navy)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(248, 250, 252);
    doc.text("VAULT", 14, 16, { charSpace: 1.4 });
    // Sub-brand text under wordmark
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(200, 200, 200);
    doc.text("CREDIT CARD · CONTROL CENTER", 14, 23);
    // Timestamp right-aligned
    doc.setFontSize(8);
    doc.setTextColor(220, 220, 220);
    doc.text(`Generated ${nowStamp()}`, w - 14, 11, { align: "right" });
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text("VAULT · Owner & Finance Dashboard", w - 14, 20, { align: "right" });
    // Amber accent line under strip
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.6);
    doc.line(0, 26, w, 26);

    // Report title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...STONE_900);
    doc.text(title, 14, 38);

    if (subtitle) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...STONE_500);
        doc.text(subtitle, 14, 44);
    }
}

function drawFilters(doc, filters, startY) {
    const entries = Object.entries(filters || {})
        .filter(([, v]) => v !== null && v !== undefined && v !== "" && v !== "all")
        .map(([k, v]) => `${k}: ${sanitizePdfText(v)}`);
    if (!entries.length) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(...STONE_500);
        doc.text("Filters: none applied", 14, startY);
        return startY + 5;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE_900);
    doc.text("Filters:", 14, startY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...STONE_500);
    // wrap filters
    const w = doc.internal.pageSize.getWidth() - 28 - 15;
    const text = entries.join("  ·  ");
    const lines = doc.splitTextToSize(text, w);
    doc.text(lines, 29, startY);
    return startY + Math.max(5, lines.length * 4);
}

function drawSummary(doc, summary, startY) {
    if (!summary) return startY;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...STONE_900);
    doc.text("Summary:", 14, startY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...STONE_500);
    doc.text(sanitizePdfText(summary), 33, startY);
    return startY + 5;
}

function drawFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.2);
        doc.line(14, h - 10, w - 14, h - 10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...STONE_500);
        doc.text("VAULT · Confidential — Credit Card Control Center", 14, h - 5);
        doc.text(`Page ${i} of ${pageCount}`, w - 14, h - 5, { align: "right" });
    }
}

/**
 * Internal: build the jsPDF document without saving. Used by both
 * `exportPdf` (download) and `generatePdfBlob` (attach-to-email).
 */
function buildPdfDoc({ title, filters, summary, columns, rows }) {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    drawHeader(doc, title, "Corporate Report");
    let y = 50;
    y = drawFilters(doc, filters, y);
    if (summary) y = drawSummary(doc, summary, y) + 2;
    else y += 2;

    const head = [columns.map((c) => c.label)];
    const body = rows.map((r) => columns.map((c) => renderCell(r, c)));

    autoTable(doc, {
        head,
        body,
        startY: y,
        theme: "grid",
        styles: {
            font: "helvetica",
            fontSize: 8,
            cellPadding: { top: 2.2, right: 2.5, bottom: 2.2, left: 2.5 },
            textColor: STONE_900,
            lineColor: [230, 230, 230],
            lineWidth: 0.1,
        },
        headStyles: {
            fillColor: NAVY,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8,
            halign: "left",
        },
        alternateRowStyles: { fillColor: STONE_100 },
        columnStyles: columns.reduce((acc, c, i) => {
            if (c.money || c.align === "right") acc[i] = { halign: "right", cellWidth: "auto" };
            return acc;
        }, {}),
        margin: { left: 14, right: 14, top: 28 },
        didDrawPage: (data) => {
            // Repeat compact brand header (with wordmark) on subsequent pages
            if (data.pageNumber > 1) {
                const w = doc.internal.pageSize.getWidth();
                doc.setFillColor(...NAVY);
                doc.rect(0, 0, w, 14, "F");
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(248, 250, 252);
                doc.text("VAULT", 14, 9, { charSpace: 1 });
                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
                doc.setTextColor(220, 220, 220);
                doc.text(title, w - 14, 9, { align: "right" });
            }
        },
    });

    drawFooter(doc);
    return doc;
}

/**
 * Export a report to PDF (triggers download).
 *
 * @param {Object}  opts
 * @param {string}  opts.title      Report title (e.g. "Monthly Expense").
 * @param {Object}  [opts.filters]  Human-readable filters { "Month": "2026-02", ... }.
 * @param {string}  [opts.summary]  Right-side summary text (row count, totals, etc.).
 * @param {Array}   opts.columns    Same shape as CSV columns; supports { money, align }.
 * @param {Array}   opts.rows       Data rows.
 * @param {string}  opts.filename   Output filename (without .pdf).
 */
export function exportPdf(opts) {
    const doc = buildPdfDoc(opts);
    doc.save(`${slug(opts.filename || opts.title)}.pdf`);
}

/**
 * Build the same PDF as `exportPdf` but return it as a Blob (no download).
 * Used by the Monthly Closing → Resend email attachment flow.
 *
 * @returns {{ blob: Blob, filename: string, base64: string }}
 */
export function generatePdfBlob(opts) {
    const doc = buildPdfDoc(opts);
    const filename = `${slug(opts.filename || opts.title)}.pdf`;
    const blob = doc.output("blob");
    // jspdf produces raw PDF bytes as a Data URI too — the base64 portion is
    // exactly what Resend expects for `attachments[].content`.
    const dataUri = doc.output("datauristring");
    const base64 = dataUri.substring(dataUri.indexOf(",") + 1);
    return { blob, filename, base64 };
}
